'use client';

import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { tw } from '@tw';
import { fitNameSize } from '@/lib/utils/fit-name';

/**
 * The page's name at poster size, stepped down only as far as its longest word
 * needs to fit the line. The stylesheet (`.sf-masthead`) owns the size; this
 * measures the words in that exact face and shrinks only when one would overflow.
 */
export function FitName({
  name,
  className,
  children,
}: {
  name: string;
  className?: string;
  /** What the heading holds; defaults to the name itself. */
  children?: ReactNode;
}) {
  const ref = useRef<HTMLHeadingElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let lastWidth = -1;
    const fit = (force = false) => {
      const width = el.clientWidth;
      // Its own font change resizes it too; only a new width is news.
      if (!force && width === lastWidth) return;
      lastWidth = width;
      el.style.fontSize = '';
      const max = parseFloat(getComputedStyle(el).fontSize);
      const probe = document.createElement('span');
      probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;';
      el.appendChild(probe);
      let word = 0;
      for (const w of name.split(/\s+/)) {
        probe.textContent = w;
        word = Math.max(word, probe.getBoundingClientRect().width);
      }
      probe.remove();
      const size = fitNameSize({ max, width, word });
      if (size < max) el.style.fontSize = `${size}px`;
    };
    fit(true);
    const observer = new ResizeObserver(() => fit());
    observer.observe(el);
    // The face may land after the first measure, and it changes every width.
    void document.fonts.ready.then(() => fit(true));
    return () => observer.disconnect();
  }, [name]);

  return (
    <h2 ref={ref} className={tw('sf-masthead min-w-0', className)}>
      {children ?? name}
    </h2>
  );
}
